import { Box, FormControl, InputLabel, MenuItem, Select, Typography } from "@mui/material";
import React, { useContext, useEffect, useMemo, useState } from "react";
import VoidCityScene from "../components/mapScenes/voidCity/VoidCityScene";
import ArenaScene from "../components/mapScenes/voidCity/ArenaScene";
import { AbstractWalletProvider } from "@abstract-foundation/agw-react";
import { abstractTestnet } from "viem/chains";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  createConfig,
  http,
  WagmiProvider,
  useAccount,
  useConnect,
  useDisconnect,
  useChainId,
  useSwitchChain,
} from "wagmi";
import { ReactNode } from "react";
import StoryModeTestUI from "../components/StoryMode/StoryModeTestUI";
import MultiMuxMixer from "../components/test/MuxPlaybackCaptureTest";
import StreamVideoAccessTest from "../components/test/StreamVideoAccessTest";
import SpinWheel from "../components/gamePlay/SpinWheel";
import DialogHUD from "../components/StoryMode/DialogHUD";
import { MainContext } from "../App";
import { useCardStore } from "../@shared/store/useCardsStore";
import InfiNFTRoulette from "../components/gamePlay/Roulette";
import { baseSepolia } from "viem/chains";
import { injected, coinbaseWallet, metaMask } from "@wagmi/connectors";
import NeoButton from "../components/NeoButton";
import TradeCardAnimation from "../components/gamePlay/TradeCardAnimation";
import cardBack from "../@shared/assets/back-of-card.png";
import SlapCardAnimation from "../components/gamePlay/SlapCardAnimation";
import CollectionEnrollmentPage from "../components/CollectionEnrollmentPage";
import PlayerPlatform from "../components/gamePlay/PlayerPlatform";
import { AnimatePresence, motion } from "framer-motion";

const queryClient = new QueryClient();

const config = createConfig({
  autoConnect: true,
  chains: [abstractTestnet, baseSepolia],
  connectors: [
    metaMask({ shimDisconnect: true }), // prevents stale connected state
    injected(), // fallback to any injected wallet
    coinbaseWallet({ appName: "InfiNFT Royale", preference: "all" }),
  ],
  transports: {
    [abstractTestnet.id]: http("https://api.testnet.abs.xyz"),
    [baseSepolia.id]: http(),
  },
});

function MetaMaskConnectButton() {
  const { address } = useAccount();
  const { connectors, connect, isPending: isConnecting } = useConnect();
  const { disconnect } = useDisconnect();
  const chainId = useChainId();
  const { switchChain, isPending: isSwitching } = useSwitchChain();

  const targetChainId = baseSepolia.id;
  const isOnBaseSepolia = chainId === targetChainId;

  const [fallbackAddress, setFallbackAddress] = React.useState(null);
  const triedSyncRef = React.useRef(false);
  const isUiConnected = !!(address || fallbackAddress);

  useEffect(() => {
    console.groupCollapsed("🔎 MM ENV CHECK");
    try {
      // Wagmi connectors summary
      console.log(
        "wagmi.connectors:",
        connectors.map((c) => ({
          id: c.id,
          name: c.name,
          ready: c.ready,
          uid: c.uid,
        }))
      );

      // Window.ethereum checks
      const eth = typeof window !== "undefined" ? window.ethereum : undefined;
      console.log("window.ethereum exists:", !!eth);

      if (eth) {
        console.log("ethereum.isMetaMask:", eth.isMetaMask);
        // Some browsers/extensions set multiple providers
        const providers =
          eth.providers ||
          (Array.isArray(eth._providers) ? eth._providers : []);
        console.log("ethereum.providers length:", providers?.length || 0);
        if (providers?.length) {
          console.log(
            "providers flags:",
            providers.map((p, i) => ({
              i,
              isMetaMask: !!p.isMetaMask,
              isCoinbaseWallet: !!p.isCoinbaseWallet,
              isBraveWallet: !!p.isBraveWallet,
            }))
          );
        }
      }
    } catch (e) {
      console.error("ENV CHECK error:", e);
    }
    console.groupEnd();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    console.log("👤 address changed:", address || "(disconnected)");
  }, [address]);

  useEffect(() => {
    console.log("🔗 chainId changed:", chainId);
  }, [chainId]);

  useEffect(() => {
    console.log(
      "🔌 connectors changed:",
      connectors.map((c) => ({
        id: c.id,
        name: c.name,
        ready: c.ready,
      }))
    );
  }, [connectors]);

  useEffect(() => {
    if (!fallbackAddress || address || triedSyncRef.current) return;
    const afterFallback = pickConnector(connectors);
    if (afterFallback?.ready) {
      triedSyncRef.current = true;
      connect({ connector: afterFallback, chainId: targetChainId })
        .then(() => {
          console.log("Effect: wagmi synced after fallback.");
        })
        .catch((e) => console.warn("Effect: wagmi sync failed:", e));
    }
  }, [fallbackAddress, address, connectors, connect, targetChainId]);

  // Prefer MetaMask by name/id, then injected, then anything
  const pickConnector = (all) => {
    if (!all || !all.length) return null;
    // Try exact ids first
    let c =
      all.find((c) => c.id === "io.metamask") ||
      all.find((c) => c.id === "metamask") || // some envs expose this
      all.find((c) => c.id === "injected");
    // Try by name next
    if (!c) c = all.find((c) => /metamask/i.test(c.name));
    // Last resort: first ready connector
    if (!c) c = all.find((c) => c.ready);
    // Absolute last: first connector
    return c || all[0] || null;
  };

  const handleConnect = async () => {
    console.groupCollapsed("🟣 MetaMask Connect Flow");
    try {
      console.log("Current chainId (wagmi):", chainId);
      console.log("Target chainId (Base Sepolia):", targetChainId);

      console.log(
        "All connectors @click:",
        connectors.map((c) => ({
          id: c.id,
          name: c.name,
          ready: c.ready,
          uid: c.uid,
        }))
      );
      const chosen = pickConnector(connectors);

      console.log(
        "Chosen connector:",
        chosen
          ? {
              id: chosen.id,
              name: chosen.name,
              ready: chosen.ready,
              uid: chosen.uid,
            }
          : null
      );

      // If wagmi didn't populate, fall back to window.ethereum
      if (!chosen) {
        console.warn(
          "No wagmi connector available — using direct EIP-1193 fallback."
        );
        await directMetaMaskFallback(targetChainId);
        // 🔁 Try again to pick a connector now that provider is authorized
        const afterFallback = pickConnector(connectors);
        if (afterFallback?.ready) {
          console.log(
            "Connecting wagmi after fallback with:",
            afterFallback.id
          );
          await connect({ connector: afterFallback, chainId: targetChainId });
        }
        console.groupEnd();
        return;
      }

      if (!chosen.ready) {
        console.warn(
          "Chosen connector not ready. Using direct EIP-1193 fallback."
        );
        await directMetaMaskFallback(targetChainId);
        const afterFallback = pickConnector(connectors);
        if (afterFallback?.ready) {
          console.log(
            "Connecting wagmi after fallback with:",
            afterFallback.id
          );
          await connect({ connector: afterFallback, chainId: targetChainId });
        }
        console.groupEnd();
        return;
      }

      console.log("Calling connect({ connector, chainId })…");
      await connect({ connector: chosen, chainId: targetChainId });
      console.log("connect() resolved.");
    } catch (err) {
      console.error("MetaMask connect error:", err);
      if (err?.cause) console.error("cause:", err.cause);
      if (err?.shortMessage) console.error("shortMessage:", err.shortMessage);
      if (err?.details) console.error("details:", err.details);
    } finally {
      console.log("Post-connect chainId (wagmi):", chainId);
      console.groupEnd();
    }
  };

  const directMetaMaskFallback = async (targetChainId) => {
    console.groupCollapsed("🟡 Direct EIP-1193 Fallback");
    try {
      const eth = typeof window !== "undefined" ? window.ethereum : undefined;
      console.log("window.ethereum:", !!eth);
      if (!eth) {
        console.warn(
          "No window.ethereum found. Extension blocked or not injected."
        );
        return;
      }

      // If multiple providers are present (MetaMask + others), pick MetaMask
      let provider = eth;
      if (Array.isArray(eth.providers) && eth.providers.length) {
        const mm = eth.providers.find((p) => p.isMetaMask);
        provider = mm || eth.providers[0];
        console.log(
          "Using provider:",
          mm ? "MetaMask (from providers[])" : "providers[0]"
        );
      } else {
        console.log("Using provider: window.ethereum");
      }
      console.log("provider.isMetaMask:", !!provider.isMetaMask);

      // Ensure Base Sepolia
      const hexChain = "0x" + targetChainId.toString(16);
      try {
        await provider.request({
          method: "wallet_switchEthereumChain",
          params: [{ chainId: hexChain }],
        });
        console.log("Switched to Base Sepolia via wallet_switchEthereumChain.");
      } catch (switchErr) {
        // 4902 = Unrecognized chain → add it, then switch
        if (switchErr?.code === 4902) {
          console.log("Chain not added. Adding Base Sepolia…");
          await provider.request({
            method: "wallet_addEthereumChain",
            params: [
              {
                chainId: hexChain,
                chainName: "Base Sepolia",
                nativeCurrency: {
                  name: "Sepolia ETH",
                  symbol: "ETH",
                  decimals: 18,
                },
                rpcUrls: ["https://sepolia.base.org"],
                blockExplorerUrls: ["https://sepolia.basescan.org/"],
              },
            ],
          });
          await provider.request({
            method: "wallet_switchEthereumChain",
            params: [{ chainId: hexChain }],
          });
          console.log("Base Sepolia added & switched.");
        } else {
          console.warn("Switch chain failed:", switchErr);
        }
      }

      // Request accounts
      const accounts = await provider.request({
        method: "eth_requestAccounts",
      });
      console.log("eth_requestAccounts =>", accounts);

      // 1) show UI immediately
      setFallbackAddress(accounts?.[0] || null);
      // 2) attempt to sync wagmi right away (if connectors are ready)
      try {
        const afterFallback = pickConnector(connectors);
        if (afterFallback?.ready && !triedSyncRef.current) {
          triedSyncRef.current = true;
          console.log(
            "Connecting wagmi after direct fallback with:",
            afterFallback.id
          );
          await connect({ connector: afterFallback, chainId: targetChainId });
        }
      } catch (e) {
        console.warn("Post-fallback wagmi connect failed:", e);
      }
    } catch (e) {
      console.error("Direct fallback error:", e);
    } finally {
      console.groupEnd();
    }
  };

  if (!isUiConnected) {
    return (
      <NeoButton
        text={isConnecting ? "Connecting…" : "Connect Wallet"}
        color="#2f6dfc"
        width={48}
        onClick={handleConnect}
        disabled={isConnecting}
      />
    );
  }

  // Connected state: show short status + disconnect
  // Connected state: show wallet info + network + disconnect
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        p: 2,
        border: "2px solid #4a4f7a",
        borderRadius: 2,
        bgcolor: "#1d2040",
        color: "#fff",
        gap: 1,
        minWidth: 260,
      }}
    >
      <Box sx={{ fontWeight: 700, fontSize: 14 }}>
        {isOnBaseSepolia ? "✅ Connected to Base Sepolia" : "⚠️ Wrong Network"}
        {!address && fallbackAddress && " (syncing…)"}
      </Box>

      {/* Full wallet address display */}
      <Box
        sx={{
          fontSize: 13,
          wordBreak: "break-all",
          background: "#2b2e4a",
          px: 1.5,
          py: 0.5,
          borderRadius: 1,
          width: "100%",
          textAlign: "center",
          fontFamily: "monospace",
        }}
      >
        {(address || fallbackAddress) ?? ""}
      </Box>

      {/* Buttons */}
      <Box sx={{ display: "flex", justifyContent: "center", gap: 1, mt: 1 }}>
        {!isOnBaseSepolia && (
          <NeoButton
            text={isSwitching ? "Switching…" : "Switch to Base Sepolia"}
            color="#6b46ff"
            width={60}
            onClick={() => switchChain({ chainId: targetChainId })}
            disabled={isSwitching}
          />
        )}
        <NeoButton
          text="Disconnect"
          color="#ef4444"
          width={40}
          onClick={() => disconnect()}
        />
      </Box>
    </Box>
  );
}

function PhantomConnectButton() {
  const [pubkey, setPubkey] = React.useState(null);
  const [connecting, setConnecting] = React.useState(false);

  // Pick the Phantom provider if available
  const getPhantomProvider = () => {
    const w = typeof window !== "undefined" ? window : undefined;
    if (!w) return null;

    // Preferred per Phantom docs
    if (w.phantom?.solana?.isPhantom) return w.phantom.solana;

    // Fallbacks: some environments expose array of providers or single window.solana
    if (Array.isArray(w.solana?.providers)) {
      const phantom = w.solana.providers.find((p) => p?.isPhantom);
      if (phantom) return phantom;
    }
    if (w.solana?.isPhantom) return w.solana;

    return null;
  };

  // Bind wallet events (account changed / disconnect)
  React.useEffect(() => {
    const provider = getPhantomProvider();
    if (!provider) return;

    const handleConnect = (args) => {
      // Some providers emit an object; prefer provider.publicKey.toString()
      const key =
        provider.publicKey?.toString?.() ||
        args?.publicKey?.toString?.() ||
        null;
      console.log("🟣 Phantom connect event →", key);
      if (key) setPubkey(key);
    };

    const handleAccountChanged = (pubKey) => {
      const key =
        pubKey?.toString?.() || provider.publicKey?.toString?.() || null;
      console.log("🔁 Phantom accountChanged →", key);
      setPubkey(key);
    };

    const handleDisconnect = () => {
      console.log("🛑 Phantom disconnect");
      setPubkey(null);
    };

    provider.on?.("connect", handleConnect);
    provider.on?.("accountChanged", handleAccountChanged);
    provider.on?.("disconnect", handleDisconnect);

    // If already connected (e.g., user approved previously), hydrate UI
    try {
      const key = provider.publicKey?.toString?.() || null;
      if (key) setPubkey(key);
    } catch {}

    return () => {
      provider.removeListener?.("connect", handleConnect);
      provider.removeListener?.("accountChanged", handleAccountChanged);
      provider.removeListener?.("disconnect", handleDisconnect);
    };
  }, []);

  const connectPhantom = async () => {
    const provider = getPhantomProvider();
    if (!provider) {
      window.open(
        "https://phantom.app/download",
        "_blank",
        "noopener,noreferrer"
      );
      return;
    }
    try {
      setConnecting(true);
      // Prompts user for permission & returns public key
      const res = await provider.connect(); // { publicKey, ... }
      const key =
        res?.publicKey?.toString?.() ||
        provider.publicKey?.toString?.() ||
        null;
      console.log("✅ Phantom connected:", key);
      setPubkey(key);
    } catch (e) {
      console.warn("Phantom connect failed:", e);
    } finally {
      setConnecting(false);
    }
  };

  const disconnectPhantom = async () => {
    const provider = getPhantomProvider();
    if (!provider) return;
    try {
      await provider.disconnect();
    } catch (e) {
      console.warn("Phantom disconnect error:", e);
    } finally {
      setPubkey(null);
    }
  };

  if (!pubkey) {
    return (
      <NeoButton
        text={connecting ? "Connecting…" : "Connect Solana (Phantom)"}
        color="#14a0ff"
        width={70}
        onClick={connectPhantom}
        disabled={connecting}
      />
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        p: 2,
        border: "2px solid #4a4f7a",
        borderRadius: 2,
        bgcolor: "#101426",
        color: "#fff",
        gap: 1,
        minWidth: 280,
      }}
    >
      <Box sx={{ fontWeight: 700, fontSize: 14 }}>✅ Connected (Phantom)</Box>
      <Box
        sx={{
          fontSize: 13,
          wordBreak: "break-all",
          background: "#1b2540",
          px: 1.5,
          py: 0.5,
          borderRadius: 1,
          width: "100%",
          textAlign: "center",
          fontFamily: "monospace",
        }}
      >
        {pubkey}
      </Box>
      <Box sx={{ display: "flex", justifyContent: "center", gap: 1, mt: 1 }}>
        <NeoButton
          text="Disconnect"
          color="#ef4444"
          width={40}
          onClick={disconnectPhantom}
        />
      </Box>
    </Box>
  );
}

const Test = () => {
  const CARD_SIZE = 140;
  const { Theme } = useContext(MainContext);
  const { infinityCards } = useCardStore();
  const card = infinityCards[0];
  console.log(card);
  const [numberSpun, setNumberSpun] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(true);
  const [showTradeAnimation, setShowTradeAnimation] = useState(false);
  const [activeCardPlayAnimation, setActiveCardPlayAnimation] = useState(null);
  const [slapVersion, setSlapVersion] = useState(1);
  const [tradeVersion, setTradeVersion] = useState(1);
  const [selectedPlayer, setSelectedPlayer] = useState(false);
  const [deckOpen, setDeckOpen] = useState(false);
  const [deckSize, setDeckSize] = useState(40);
  const testChar = {
    name: "Ashro",
    imageUrl:
      "https://firebasestorage.googleapis.com/v0/b/infinft-card-game.firebasestorage.app/o/NFT%20Images%2FAshro.png?alt=media&token=1b0ed64c-a96b-4136-9ef2-e2e4d3fc4a8a",
    stance: "attack",
  };

  const [drawReq, setDrawReq] = useState(null); // { id, count }
  const [drawCount, setDrawCount] = useState(0); // how many cards to animate out
  const [isDrawing, setIsDrawing] = useState(false);

  const startDraw = (count = 1) => {
    // clamp to deck size (so we never draw more than we have)
    const safeCount = Math.max(0, Math.min(count, deckSize));
    if (!safeCount) return;

    // force a new animation instance even if you click same count again
    setDrawReq({ id: `${Date.now()}-${Math.random()}`, count: safeCount });
  };

  // Example script (pull this from JSON, Firestore, etc.)
  const introScript = useMemo(
    () => [
      {
        speaker: {
          name: "Nox",
          portrait: require("../../src/@shared/assets/nox-start.png"),
        },
        text: "I should find my friends before heading out of town to train!",
      },
      {
        speaker: {
          name: "Tenkou",
          portrait: require("../../src/@shared/assets/azukii.png"),
        },
        text: "Hey you. You’re finally awake.",
      },
      {
        speaker: {
          name: "Blitz   ",
          portrait: require("../../src/@shared/assets/blitz-start.png"),
        },
        text: "Which way is the training grounds?",
        choices: [
          { id: "north", label: "North bridge" },
          { id: "west", label: "Old forest" },
        ],
        onChoose: (c) => {
          // route quest, set flag, etc.
          console.log("player chose:", c.id);
        },
      },
    ],
    []
  );

  // Example “interact” callback (your collision/overlap can call this)
  const onTalkToNpc = () => setDialogOpen(true);

  useEffect(() => {
    if (!drawReq?.count) return;

    // kick off
    setIsDrawing(true);
    setDeckOpen(true);
    setDrawCount(drawReq.count);

    // after the cards fly out, close the deck
    const CLOSE_DELAY_MS = 900; // matches card rise timing below
    const t = setTimeout(() => {
      setDrawCount(0);
      setDeckOpen(false);
      setIsDrawing(false);

      // optional: reduce deck size to simulate draw
      setDeckSize((prev) => Math.max(0, prev - drawReq.count));
    }, CLOSE_DELAY_MS);

    return () => clearTimeout(t);
  }, [drawReq]);

  return (
    <QueryClientProvider client={queryClient}>
      <WagmiProvider config={config}>
        {/* <CollectionEnrollmentPage/> */}

        {/* <Box
          sx={{
            position: "relative",
            flexDirection: "column",
            width: "100%",
            height: "100dvh",
            overflowY: "scroll",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "#000",
          }}
        >
          <Box
            sx={{
              height: "30%",
              aspectRatio: "3/4",
              position: "relative",
              containerType: "inline-size",
            }}
          >
            {!deckOpen ? (
              <>
                <img
                  src={require("../@shared/assets/battleboards/deck-closed.png")}
                  style={{
                    height: "100%",
                    position: "absolute",
                    left: "50%",
                    transform: "translateX(-50%)",
                    zIndex: 0,
                  }}
                  alt=""
                />
                <Typography
                  style={{
                    fontSize: "25cqw",
                    fontWeight: "bold",
                    color: "#fff",
                    position: "absolute",
                    left: "12%",
                    transform: ` rotateY(-35deg) rotateX(-1deg) skewY(12deg)`,
                    top: `40%`,
                  }}
                >
                  {deckSize}
                </Typography>
              </>
            ) : (
              <>
                <img
                  src={require("../@shared/assets/battleboards/deck-opened-back.png")}
                  style={{
                    height: "100%",
                    position: "absolute",
                    left: "50%",
                    transform: "translateX(-50%)",
                    zIndex: 0,
                  }}
                  alt=""
                />

                <AnimatePresence mode="popLayout">
                  {drawCount > 0 &&
                    Array.from({ length: drawCount }).map((_, idx) => {
                      // stagger + slight horizontal spread
                      const spreadX = (idx - (drawCount - 1) / 2) * 6; // px-ish
                      const delay = idx * 0.15;

                      return (
                        <motion.img
                          key={`${drawReq?.id || "draw"}-${idx}`}
                          src={cardBack}
                          alt=""
                          initial={{
                            opacity: 0,
                            y: 18,
                            x: spreadX,
                            scale: 0.98,
                          }}
                          animate={{
                            opacity: 1,
                            y: -78 - idx * 6, // rise out of case
                            x: spreadX,
                            scale: 1,
                            transition: {
                              duration: 0.35,
                              delay,
                              ease: "easeOut",
                            },
                          }}
                          exit={{
                            opacity: 0,
                            y: -120 - idx * 10, // keep going up as it fades
                            transition: {
                              duration: 0.25,
                              ease: "easeIn",
                            },
                          }}
                          style={{
                            height: "70%",
                            position: "absolute",
                            left: "15%",
                            transform: `translateX(-${
                              50 - idx
                            }%) rotateY(-35deg) rotateX(-1deg) skewY(12deg)`,
                            top: `${20 - idx * 0.33}%`,
                            zIndex: 60 - idx,
                          }}
                        />
                      );
                    })}
                </AnimatePresence>
                {Array.from({ length: deckSize }).map((card, idx) => {
                  return (
                    <img
                      src={cardBack}
                      style={{
                        height: "70%",
                        position: "absolute",
                        left: "50%",
                        transform: `translateX(-${
                          75 - idx
                        }%) rotateY(-35deg) rotateX(-1deg) skewY(12deg)`,
                        top: `${20 - idx * 0.33}%`,
                        zIndex: 99 - (idx),
                      }}
                      alt=""
                    />
                  );
                })}

                <img
                  src={require("../@shared/assets/battleboards/deck-opened-front.png")}
                  style={{
                    height: "100%",
                    position: "absolute",
                    left: "50%",
                    transform: "translateX(-50%)",
                    zIndex: 100,
                  }}
                  alt=""
                />
              </>
            )}
          </Box>

          <MetaMaskConnectButton />
          <PhantomConnectButton />

          {showTradeAnimation && (
            <TradeCardAnimation
              version={tradeVersion}
              onFinish={() => {
                setShowTradeAnimation(false);
              }}
            />
          )}

          {activeCardPlayAnimation && (
            <SlapCardAnimation
              card={activeCardPlayAnimation.card}
              version={activeCardPlayAnimation.version}
              onFinish={() => {
                console.log("animated");
                // if (targetPlayer) {
                //   setActiveAnimation({
                //     type: "playCard",
                //     target: 'Test',
                //     timestamp: activeCardPlayAnimation.timestamp,
                //     fps: 18,
                //   });
                // }

                // 💥 Clean it up
                setActiveCardPlayAnimation(null);
              }}
            />
          )}

          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              flexWrap: "wrap",
              justifyContent: "space-evenly",
              transform: "skewX(-50deg)",
              width: { xs: "50%", md: "18%" },
              marginRight: { xs: "15%", md: "7%" },
              marginBottom: { xs: "0%", md: "10%" },
            }}
          >
            <PlayerPlatform
              id={"11111234567890"}
              name={"Test"}
              character={testChar}
              isBot={false}
              playerTurn={true}
              isSelected={false}
              onSelect={() => setSelectedPlayer(!selectedPlayer)}
              activeAnimation={null}
              cpMovesLeft={2}
              playerMoveType={null}
              lastMove={{
                target: "Ashro",
                defender: "Ashro",
                type: "playCard",
                card: {
                  imageUrl:
                    "https://firebasestorage.googleapis.com/v0/b/infinft-card-game.firebasestorage.app/o/NFT%20Images%2FAshro.png?alt=media&token=1b0ed64c-a96b-4136-9ef2-e2e4d3fc4a8a",
                  areaAffected: ["Health"],
                  effect:
                    "Restores 1 health per turn for 3 turns (total 3 health).",
                },
              }}
              coOp={false}
              playerEmote={null}
            />
          </Box>

          <NeoButton
            color="#10b981"
            width={20}
            onClick={() => startDraw(1)}
            text={"draw 1"}
          />
          <NeoButton
            color="#22c55e"
            width={20}
            onClick={() => startDraw(3)}
            text={"draw 3"}
          />
          <NeoButton
            color={Theme.palette.secondary.main}
            width={20}
            onClick={() => setShowTradeAnimation(true)}
            text={`trade v${tradeVersion}`}
          />
          <NeoButton
            color={Theme.palette.tirciary.main}
            width={20}
            onClick={() =>
              setActiveCardPlayAnimation({
                type: "playCard",
                version: slapVersion,
                player: {
                  name: "test",
                },
                target: {
                  name: "sam",
                },
                card: card,
                timestamp: Date.now(),
              })
            }
            text={`play v${slapVersion}`}
          />
          <FormControl
            size="small"
            sx={{
              minWidth: 130,
              backgroundColor: "rgba(20,20,20,0.85)",
              borderRadius: "8px",
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "rgba(255,255,255,0.4)",
              },
              "& .MuiSvgIcon-root, & .MuiInputLabel-root, & .MuiSelect-select": {
                color: "#fff",
              },
            }}
          >
            <InputLabel id="slap-version-select-label">Slap Ver</InputLabel>
            <Select
              labelId="slap-version-select-label"
              id="slap-version-select"
              value={slapVersion}
              label="Slap Ver"
              onChange={(e) => setSlapVersion(Number(e.target.value))}
            >
              <MenuItem value={1}>v1 Classic</MenuItem>
              <MenuItem value={2}>v2 Cinematic</MenuItem>
              <MenuItem value={3}>v3 Whip In</MenuItem>
              <MenuItem value={4}>v4 Drop Slam</MenuItem>
              <MenuItem value={5}>v5 Table Slide</MenuItem>
              <MenuItem value={6}>v6 Dealer Flick</MenuItem>
              <MenuItem value={7}>v7 Double Knock</MenuItem>
              <MenuItem value={8}>v8 Spin Toss</MenuItem>
              <MenuItem value={9}>v9 Reveal</MenuItem>
              <MenuItem value={10}>v10 Fall Forward</MenuItem>
            </Select>
          </FormControl>
          <FormControl
            size="small"
            sx={{
              minWidth: 130,
              backgroundColor: "rgba(20,20,20,0.85)",
              borderRadius: "8px",
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "rgba(255,255,255,0.4)",
              },
              "& .MuiSvgIcon-root, & .MuiInputLabel-root, & .MuiSelect-select": {
                color: "#fff",
              },
            }}
          >
            <InputLabel id="trade-version-select-label">Trade Ver</InputLabel>
            <Select
              labelId="trade-version-select-label"
              id="trade-version-select"
              value={tradeVersion}
              label="Trade Ver"
              onChange={(e) => setTradeVersion(Number(e.target.value))}
            >
              <MenuItem value={1}>v1 Classic</MenuItem>
              <MenuItem value={2}>v2 3D Shuffle</MenuItem>
              <MenuItem value={3}>v3 Table Pass</MenuItem>
              <MenuItem value={4}>v4 Stacked Cut</MenuItem>
              <MenuItem value={5}>v5 Orbital Spin</MenuItem>
              <MenuItem value={6}>v6 Boomerang</MenuItem>
              <MenuItem value={7}>v7 Helix</MenuItem>
              <MenuItem value={8}>v8 Mirror Snap</MenuItem>
            </Select>
          </FormControl>
        </Box> */}

        {/*<AbstractWalletProvider chain={abstractTestnet}>
           <Box
            sx={{
              position: "relative",
              width: "100%",
              height: "100dvh",
              overflowY: "scroll",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          > */}
        {/* <SpinWheel
              mode="daily"
              onReward={(reward) => {
                console.log("reward", reward);
                // reward = { type: "token"|"xp"|"boost"|"card", amount: number, icon: string, label: string }
                // ➜ grant user reward here, show toast/modal, etc.
              }}
            /> */}
        {/* <DialogHUD
        theme={Theme}
        open={dialogOpen}
        script={introScript}
        onClose={() => setDialogOpen(false)}
      /> */}
        {/* <InfiNFTRoulette /> */}
        {/* <MetaMaskConnectButton /> */}

        <StoryModeTestUI/>
        {/* <MultiMuxMixer/> */}
        {/* <StreamVideoAccessTest/> */}
        {/* <VoidCityScene></VoidCityScene> */}
        {/* </Box>
        </AbstractWalletProvider> */}
      </WagmiProvider>
    </QueryClientProvider>
  );
};

export default Test;
